import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { HelpCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useGuide, useGuideCategoriesBySlug, useGuideFAQs } from '../hooks/useGuides';
import { GuideFAQModal } from '../components/GuideFAQModal';
import type { GuideFAQ } from '../types';

export function FAQDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('guides');
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFAQ, setSelectedFAQ] = useState<GuideFAQ | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const { data: guide, isLoading: isLoadingGuide } = useGuide(slug || '');
  const { data: categories, isLoading: isLoadingCategories } = useGuideCategoriesBySlug(slug || '');
  const { data: faqs, isLoading: isLoadingFAQs } = useGuideFAQs(guide?.id || '');

  // Handle direct link to FAQ via URL param
  useEffect(() => {
    const faqId = searchParams.get('faq');
    if (faqId && faqs) {
      const faq = faqs.find((f) => f.id === faqId);
      if (faq) {
        setSelectedFAQ(faq);
        // Expand the category
        if (faq.category_id) {
          setExpandedCategories((prev) => new Set([...prev, faq.category_id!]));
        }
        // Clean up URL
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, faqs, setSearchParams]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Group FAQs by category
  const faqsByCategory = faqs?.reduce((acc, faq) => {
    const catId = faq.category_id || 'uncategorized';
    if (!acc[catId]) acc[catId] = [];
    acc[catId].push(faq);
    return acc;
  }, {} as Record<string, GuideFAQ[]>) || {};

  const isLoading = isLoadingGuide || isLoadingCategories || isLoadingFAQs;

  if (isLoading) {
    return (
      <>
        <h1 className="text-white text-xl font-semibold mb-6">{t('breadcrumbs.home')}</h1>
        <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
          <HelpCircle className="w-4 h-4" />
          <span>{t('breadcrumbs.faq')}</span>
          <ChevronRight className="w-4 h-4" />
          <div className="h-4 bg-white/20 rounded w-24 animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="glass-effect rounded-[15px] p-4 animate-pulse">
              <div className="h-6 bg-white/10 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (!guide) {
    return (
      <>
        <h1 className="text-white text-xl font-semibold mb-6">{t('breadcrumbs.home')}</h1>
        <div className="text-center py-12">
          <p className="text-white/70">{t('faqDetail.notFound')}</p>
          <button
            onClick={() => navigate('/tools/guides/fragen')}
            className="mt-4 text-pp-orange hover:text-pp-orange/80"
          >
            {t('faqDetail.backToOverview')}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-6">{t('breadcrumbs.home')}</h1>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-white/80 text-sm mb-8">
        <HelpCircle className="w-4 h-4" />
        <span>{t('breadcrumbs.faq')}</span>
        <ChevronRight className="w-4 h-4" />
        <span>{guide.title}</span>
      </div>

      {/* Categories with FAQs */}
      <div className="space-y-3">
        {categories?.map((category) => {
          const categoryFAQs = faqsByCategory[category.id] || [];
          const isExpanded = expandedCategories.has(category.id);

          if (categoryFAQs.length === 0) return null;

          return (
            <div key={category.id} className="glass-effect rounded-[15px] overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-white">{category.name}</span>
                  <span className="text-xs text-pp-dark bg-white px-2 py-0.5 rounded-[2.5px]">
                    {categoryFAQs.length}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-white/60" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-pp-orange" />
                )}
              </button>

              {/* FAQs List */}
              {isExpanded && (
                <div className="border-t border-white/10">
                  {categoryFAQs.map((faq, idx) => (
                    <button
                      key={faq.id}
                      onClick={() => setSelectedFAQ(faq)}
                      className={`w-full px-6 py-3 text-left hover:bg-white/5 transition-colors flex items-center justify-between ${
                        idx !== categoryFAQs.length - 1 ? 'border-b border-white/10' : ''
                      }`}
                    >
                      <span className="text-white/90 line-clamp-2 pr-2">{faq.question}</span>
                      <ChevronRight className="w-4 h-4 text-pp-orange flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Uncategorized FAQs */}
        {faqsByCategory['uncategorized']?.length > 0 && (
          <div className="glass-effect rounded-[15px] overflow-hidden">
            <button
              onClick={() => toggleCategory('uncategorized')}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="font-medium text-white">{t('faqDetail.uncategorized')}</span>
                <span className="text-xs text-pp-dark bg-white px-2 py-0.5 rounded-[2.5px]">
                  {faqsByCategory['uncategorized'].length}
                </span>
              </div>
              {expandedCategories.has('uncategorized') ? (
                <ChevronDown className="w-5 h-5 text-white/60" />
              ) : (
                <ChevronRight className="w-5 h-5 text-pp-orange" />
              )}
            </button>

            {expandedCategories.has('uncategorized') && (
              <div className="border-t border-white/10">
                {faqsByCategory['uncategorized'].map((faq, idx) => (
                  <button
                    key={faq.id}
                    onClick={() => setSelectedFAQ(faq)}
                    className={`w-full px-6 py-3 text-left hover:bg-white/5 transition-colors flex items-center justify-between ${
                      idx !== faqsByCategory['uncategorized'].length - 1 ? 'border-b border-white/10' : ''
                    }`}
                  >
                    <span className="text-white/90 line-clamp-2 pr-2">{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-pp-orange flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FAQ Detail Modal */}
      {selectedFAQ && (
        <GuideFAQModal
          faq={selectedFAQ}
          onClose={() => setSelectedFAQ(null)}
        />
      )}
    </>
  );
}
